import { IonIcon } from "@ionic/react";
import { alertCircleOutline, chevronBackOutline, refreshOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { PayrexxCheckoutService } from "../../services/PayrexxCheckoutService";
import { TicketPurchaseApi } from "../../services/TicketPurchaseApi";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ephemeralStore, TICKET_PURCHASE_DEFAULT, ticketPurchaseSlice, useEphemeralSelector } from "../../store/ephemeral";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { cancelPendingPurchasePayment, PAYMENT_SECURED_STATUSES } from "./payrexxReturn.tools";
import { TicketPurchaseLoader } from "./TicketPurchaseLoader";
import { readPurchaseError } from "./ticketPurchaseOfferRefresh.tools";

interface TicketPaymentProps { "isDarkUi": boolean }

/**
 * Payment step of the ticket purchase wizard (Payrexx hosted checkout).
 *
 * The order summary acts as the purchase confirmation, so a fresh prebook
 * starts the checkout automatically: creates a Payrexx gateway through the
 * backend, then sends the shopper to the hosted payment page — full-page
 * redirect on web, in-app browser on native. This step is a progress screen;
 * interactive controls only appear to resume an in-flight checkout or retry
 * after a failure.
 * The shopper's return is handled by `PayrexxReturnPage` (web) /
 * `PayrexxNativeReturnBootstrap` (native), which re-verify the payment through
 * the backend; this component also offers a manual "Vérifier" path.
 */
export const TicketPayment: React.FC<TicketPaymentProps> = ( { isDarkUi } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const state = useEphemeralSelector( ticketPurchaseSlice, ( s: TicketPurchaseState ) => s );
    const { prebookingResponse } = state;

    /** Guards the auto-advance/creation against StrictMode double-invocation. */
    const advanceHandledRef = useRef( false );

    /** Guards the checkout auto-start against StrictMode double-invocation. */
    const autoStartedRef = useRef( false );

    const purchaseAttemptId = useMemo(
        () => state.purchaseAttemptId ?? prebookingResponse?.purchaseAttemptId ?? "",
        [ state.purchaseAttemptId, prebookingResponse ],
    );

    // A secured reservation (or captured payment) advances to the booking step.
    useEffect( () => {
        if ( !PAYMENT_SECURED_STATUSES.has( state.paymentStatus ) || advanceHandledRef.current ) {
            return;
        }
        advanceHandledRef.current = true;
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "step":     "confirmation",
            "canClose": false,
        } ) );
    }, [ state.paymentStatus ] );

    const startPayment = useCallback( async() => {
        if ( purchaseAttemptId === "" || state.paymentStatus === "creating" ) {
            return;
        }

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "paymentStatus": "creating",
            "canClose":      false,
            "error":         null,
        } ) );

        try {
            const gateway = await TicketPurchaseApi.postPurchasePaymentGateway( {
                purchaseAttemptId,
                "platform":     PayrexxCheckoutService.isNativeCheckout() ? "native" : "web",
                "checkoutMode": state.checkoutMode,
            } );

            if ( PAYMENT_SECURED_STATUSES.has( gateway.status ) || gateway.redirectUrl === null ) {
                // Idempotent retry: an earlier gateway already secured the payment.
                ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                    ...prev,
                    "paymentId":     gateway.paymentId,
                    "paymentStatus": gateway.status,
                } ) );

                return;
            }

            /*
             * `awaiting_return` exists only on native: on web the full-page
             * redirect unloads the app with the "creating" spinner still up, and
             * the resume rebuilds the state from the backend anyway.
             */
            const isNative = PayrexxCheckoutService.isNativeCheckout();

            await PayrexxCheckoutService.openCheckout( gateway.redirectUrl );

            if ( isNative ) {
                ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                    ...prev,
                    "paymentId":     gateway.paymentId,
                    "payrexxUrl":    gateway.redirectUrl,
                    "paymentStatus": "awaiting_return",

                    // Closing the modal while awaiting return cancels the payment (see TicketPurchaseModal).
                    "canClose": true,
                } ) );
            }
        } catch( error ) {
            const { code } = readPurchaseError( error );

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "paymentStatus": "failed",
                "canClose":      true,
                "error":         code === "PURCHASE_ATTEMPT_CLOSED"
                    ? "Cet achat est clôturé. Fermez puis recommencez l'achat depuis l'itinéraire."
                    : code === "PAYMENT_GATEWAY_IN_PROGRESS"
                        ? "Une demande de paiement est déjà en cours. Réessayez dans quelques secondes."
                        : code === "PAYMENT_TOKEN_RESERVATION_FAILED"
                            ? "Le paiement en un clic n'a pas pu être confirmé. Réessayez ; aucun montant ne sera débité."
                            : error instanceof Error ? error.message : "Erreur de paiement",
            } ) );
        }
    }, [ purchaseAttemptId, state.checkoutMode, state.paymentStatus ] );

    /*
     * The order summary already acted as the purchase confirmation: a fresh
     * prebook lands here with `idle` and the hosted checkout starts without a
     * second click. Resumed returns never arrive `idle` (they carry
     * `awaiting_return`/`pending`/`failed`/secured), so this cannot loop.
     */
    useEffect( () => {
        if ( state.paymentStatus !== "idle" || purchaseAttemptId === "" || autoStartedRef.current ) {
            return;
        }

        autoStartedRef.current = true;
        void startPayment();
    }, [ state.paymentStatus, purchaseAttemptId, startPayment ] );

    const goBack = useCallback( () => {
        /*
         * Same payment cleanup as the modal close: a still-pending gateway is
         * cancelled so no money stays blocked on an abandoned attempt, and the
         * payment state resets so the summary no longer shows the error.
         */
        if ( purchaseAttemptId !== "" ) {
            void cancelPendingPurchasePayment( purchaseAttemptId );
        }

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "step":          "summary",
            "paymentId":     null,
            "payrexxUrl":    null,
            "paymentStatus": "idle",
            "error":         null,
        } ) );
    }, [ purchaseAttemptId ] );

    /*
     * Same semantics as the modal close button: a still-pending payment is
     * cancelled (its gateway deleted), then the whole wizard state resets.
     */
    const cancelPurchase = useCallback( () => {
        if ( purchaseAttemptId !== "" ) {
            void cancelPendingPurchasePayment( purchaseAttemptId );
        }

        ephemeralStore.setValue( ticketPurchaseSlice, TICKET_PURCHASE_DEFAULT );
    }, [ purchaseAttemptId ] );

    const contentBackground = isDarkUi ? "rgba(0, 0, 0, 0.22)" : "rgba(15, 23, 42, 0.05)";
    const isBusy = state.paymentStatus === "creating" || state.paymentStatus === "verifying";
    const awaitingReturn = state.paymentStatus === "awaiting_return";
    const canRetry = !isBusy &&
      ( state.paymentStatus === "failed" || state.paymentStatus === "pending" || awaitingReturn );

    return (
        <div className="flex h-full flex-col">
            <div
                className="flex-1 overflow-y-auto p-[12px]"
                style={{ "backgroundColor": contentBackground }}
            >
                {prebookingResponse === null && purchaseAttemptId === ""
? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                        {state.error !== null
? (
                            <>
                                <IonIcon
                                    aria-hidden="true"
                                    className="text-red-500"
                                    icon={alertCircleOutline}
                                    style={{ "fontSize": "28px" }}
                                />
                                <p className="text-sm font-semibold">
                                    La préparation de votre commande a échoué.
                                </p>
                                <p className="text-xs text-red-500">
                                    {state.error}
                                </p>
                            </>
                        )
: (
                            <>
                                <TicketPurchaseLoader isDarkUi={isDarkUi} />
                                <p
                                    className="text-sm"
                                    style={{ "color": palette.overlayTextSecondary }}
                                >
                                    Préparation de votre commande…
                                </p>
                            </>
                        )}
                    </div>
                )
: (
                <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
                {state.error !== null
? (
                    <>
                        <IonIcon
                            aria-hidden="true"
                            className="text-red-500"
                            icon={alertCircleOutline}
                            style={{ "fontSize": "28px" }}
                        />
                        <p className="text-xs text-red-500">
                            {state.error}
                        </p>
                    </>
                )
: (
                    <>
                        <TicketPurchaseLoader isDarkUi={isDarkUi} />
                        <p
                            className="text-xs"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            {awaitingReturn
                                ? "Paiement en cours sur la page Payrexx. Une fois terminé, votre commande reprend automatiquement."
                                : state.paymentStatus === "verifying"
                                    ? "Vérification du paiement…"
                                    : state.checkoutMode === "direct"
                                        ? "Paiement en un clic en cours…"
                                        : "Redirection vers la page de paiement sécurisée…"}
                        </p>
                    </>
                )}
                {!isBusy && (
                    <div className="flex w-full max-w-xs flex-col gap-2">
                        {canRetry && (
                            <button
                                className="flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                disabled={isBusy}
                                onClick={() => void startPayment()}
                                style={{ "backgroundColor": palette.brandPrimary }}
                                type="button"
                            >
                                <IonIcon
                                    aria-hidden="true"
                                    icon={refreshOutline}
                                    style={{ "fontSize": "16px" }}
                                />
                                Réessayer
                            </button>
                        )}
                        <div className="flex gap-2">
                            <button
                                className="flex flex-1 items-center justify-center gap-1 rounded-xl border px-3 py-3 text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
                                disabled={isBusy}
                                onClick={goBack}
                                style={{
                                    "backgroundColor": palette.interactiveHoverBackground,
                                    "borderColor":     palette.overlayBorderStrong,
                                    "color":           palette.modalCardTextPrimary,
                                }}
                                type="button"
                            >
                                <IonIcon
                                    aria-hidden="true"
                                    icon={chevronBackOutline}
                                    style={{ "fontSize": "16px" }}
                                />
                                Retour
                            </button>
                            <button
                                className="flex flex-1 items-center justify-center rounded-xl border px-3 py-3 text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-40"
                                disabled={isBusy}
                                onClick={cancelPurchase}
                                style={{
                                    "borderColor": palette.overlayBorderStrong,
                                    "color":       palette.overlayTextSecondary,
                                }}
                                type="button"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                )}
                </div>
                )}
            </div>
        </div>
    );
};
